import { useEffect, useState } from 'react';
import { Link, Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { hasFactors } from './api';
import { useSession } from './session';
import { ToastProvider } from './toast';
import { NavProvider } from './nav';
import { Icon } from './components/Icon';
import { Sidebar } from './components/Sidebar';
import { Account } from './pages/Account';
import { Audit } from './pages/Audit';
import { AcceptInvite } from './pages/AcceptInvite';
import { Enrol } from './pages/Enrol';
import { Errors } from './pages/Errors';
import { ConnectError } from './pages/ConnectError';
import { Consent } from './pages/Consent';
import { Device } from './pages/Device';
import { Home } from './pages/Home';
import { InviteCodes } from './pages/InviteCodes';
import { Console } from './pages/admin/Console';
import { SigningKeys } from './pages/admin/SigningKeys';
import { Workspaces } from './pages/admin/Workspaces';
import { Login } from './pages/Login';
import { Org } from './pages/Org';
import { SignUp } from './pages/SignUp';
import { Skill } from './pages/Skill';
import { SkillAudit } from './pages/SkillAudit';
import { Skills } from './pages/Skills';
import { OrgConnections } from './pages/org/Connections';
import { OrgGrants } from './pages/org/Grants';
import { WorkspaceGrants } from './pages/workspace/Grants';
import { OrgSettings } from './pages/org/Settings';
import { NewOrg } from './pages/NewOrg';
import { OpenOrg } from './pages/OpenOrg';
import { Mfa } from './pages/Mfa';
import { MfaResetComplete } from './pages/MfaResetComplete';
import { MfaResetRequest } from './pages/MfaResetRequest';
import { MfaResets } from './pages/MfaResets';
import { Workspace } from './pages/Workspace';
import { WorkspaceOrgs } from './pages/workspace/Orgs';
import { WorkspaceMembers } from './pages/workspace/Members';
import { WorkspaceSettings } from './pages/workspace/Settings';

/** Gate on how far the sign-in has got: a password session, or a fully verified one. */
function Require({ level }: { level: 'password' | 'mfa' }) {
  const { loading, me } = useSession();
  const location = useLocation();
  // Where to come back to once signed in. Without this an OAuth consent link, which is usually opened in
  // a browser that is not signed in yet, dies at the sign-in screen and the agent waits for a code that
  // is never coming.
  const returnTo = `${location.pathname}${location.search}`;
  if (loading) return <p className="muted">Loading…</p>;
  if (!me) return <Navigate to="/login" replace state={{ returnTo }} />;
  if (level === 'mfa' && !me.mfaVerified) {
    return <Navigate to={hasFactors(me) ? '/mfa' : '/enrol'} replace state={{ returnTo }} />;
  }
  return <Outlet />;
}

/**
 * Pages that are a tool rather than a document.
 *
 * The content column is 1040px because that is a comfortable measure for prose, and most of this console
 * is prose with controls in it. The skill editor is not: it is two panes read against each other, one of
 * them a grid of four decisions per field, and at that width the permission columns scrolled sideways out
 * of sight. Widening everything would fix it by making every other page's paragraphs too long to read.
 */
const WIDE_ROUTES = [/\/skills\/[^/]+$/];

function Shell() {
  const { me, productName, signOut } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const wide = WIDE_ROUTES.some((r) => r.test(location.pathname));

  // The drawer closes on any navigation, Back and Forward included, and when the session changes under it.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.key, me]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Somebody still signing in has nowhere to go yet, so they get the page and nothing else - except a way
  // to end the password session, which matters most on a shared machine somebody walks away from mid-setup.
  if (!me?.mfaVerified) {
    return (
      <div className="shell bare">
        <header className="bare-head">
          <Link to="/" className="brand">{productName}</Link>
          {me && (
            <button type="button" className="secondary compact" onClick={() => signOut().then(() => navigate('/login'))}>
              Sign out
            </button>
          )}
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <NavProvider>
      <div className={menuOpen ? 'shell nav-open' : 'shell'}>
        {/* On a phone the menu is a drawer, and this bar is what opens it. Hidden on a wide screen. */}
        <header className="mobilebar">
          <button
            type="button"
            className="secondary"
            aria-label="Open the menu"
            aria-expanded={menuOpen}
            aria-controls="app-menu"
            onClick={() => setMenuOpen(true)}
          >
            <Icon name="menu" />
          </button>
          <Link to="/" className="brand">{productName}</Link>
        </header>
        <Sidebar onNavigate={() => setMenuOpen(false)} />
        {menuOpen && <div className="scrim" onClick={() => setMenuOpen(false)} />}
        <main className={wide ? 'content wide' : 'content'}>
          <Outlet />
        </main>
      </div>
    </NavProvider>
  );
}

/**
 * The sidebar moves straight from one org to another, and both are the same route, so without a key React
 * would keep the page mounted: the last org's header, its Salesforce users and any half-filled form would
 * sit under the new org's address until the new one loaded, and for good if it failed to. Keying on the id
 * gives each org, and each workspace, a page of its own.
 */
function WorkspaceRoute() {
  const { tenantId } = useParams();
  return <Workspace key={tenantId} />;
}

function OrgRoute() {
  const { tenantId, orgId } = useParams();
  return <Org key={`${tenantId}/${orgId}`} />;
}

export function App() {
  return (
    <ToastProvider>
      <Routes>
      <Route element={<Shell />}>
        <Route path="/login" element={<Login />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/signup/:code" element={<SignUp />} />
        <Route path="/forgot-factors" element={<MfaResetRequest />} />
        <Route path="/mfa-reset/:token" element={<MfaResetComplete />} />
        <Route element={<Require level="password" />}>
          <Route path="/mfa" element={<Mfa />} />
          <Route path="/enrol" element={<Enrol />} />
        </Route>
        <Route element={<Require level="mfa" />}>
          <Route path="/" element={<Home />} />
          <Route path="/open" element={<OpenOrg />} />
          <Route path="/workspaces/:tenantId" element={<WorkspaceRoute />}>
            <Route index element={<WorkspaceOrgs />} />
            <Route path="members" element={<WorkspaceMembers />} />
            <Route path="grants" element={<WorkspaceGrants />} />
            <Route path="audit" element={<Audit />} />
            <Route path="skill-log" element={<SkillAudit />} />
            <Route path="settings" element={<WorkspaceSettings />} />
          </Route>
          <Route path="/workspaces/:tenantId/orgs/new" element={<NewOrg />} />
          <Route path="/workspaces/:tenantId/orgs/:orgId" element={<OrgRoute />}>
            <Route index element={<OrgConnections />} />
            <Route path="grants" element={<OrgGrants />} />
            <Route path="skills" element={<Skills />} />
            <Route path="sessions" element={<Audit />} />
            <Route path="skill-log" element={<SkillAudit />} />
            <Route path="settings" element={<OrgSettings />} />
          </Route>
          {/* The skill editor is wide and has its own header, so it is not inside the org's page. It sits
              under the org in the address so the menu can show which org it belongs to. */}
          <Route path="/workspaces/:tenantId/orgs/:orgId/skills/:skillId" element={<Skill />} />
          <Route path="/workspaces/:tenantId/skills/:skillId" element={<Skill />} />
          {/* Links that went out before the rename, and anything Salesforce redirects back to. */}
          <Route path="/tenants/:tenantId" element={<LegacyTenant />} />
          <Route path="/tenants/:tenantId/orgs/:orgId" element={<LegacyOrg />} />
          <Route path="/tenants/:tenantId/audit" element={<LegacyAudit />} />
          <Route path="/connect-error" element={<ConnectError />} />
          <Route path="/device" element={<Device />} />
          <Route path="/consent" element={<Consent />} />
          <Route path="/account" element={<Account />} />
          <Route path="/mfa-resets" element={<MfaResets />} />
          {/*
            The console. Its pages are reachable at the paths they always had, so a link an operator
            saved still works, but they are children of one shell now rather than four unrelated entries
            in the top navigation.
          */}
          <Route path="/admin" element={<Console />}>
            <Route index element={<Navigate to="/admin/keys" replace />} />
            <Route path="keys" element={<SigningKeys />} />
            <Route path="workspaces" element={<Workspaces />} />
            <Route path="errors" element={<Errors />} />
            <Route path="invites" element={<InviteCodes />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
      </Routes>
    </ToastProvider>
  );
}

/**
 * The pages moved when "organisation" turned out to mean two different things. Old links are redirected
 * rather than broken: they are in bookmarks, in tickets, and in the Salesforce redirect an org comes back
 * through after connecting.
 */
function LegacyTenant() {
  const { tenantId } = useParams();
  return <Navigate to={`/workspaces/${tenantId}`} replace />;
}

function LegacyOrg() {
  const { tenantId, orgId } = useParams();
  const [params] = useSearchParams();
  const query = params.toString();
  return <Navigate to={`/workspaces/${tenantId}/orgs/${orgId}${query ? `?${query}` : ''}`} replace />;
}

function LegacyAudit() {
  const { tenantId } = useParams();
  const [params] = useSearchParams();
  const query = params.toString();
  return <Navigate to={`/workspaces/${tenantId}/audit${query ? `?${query}` : ''}`} replace />;
}
